import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useParams } from "react-router-dom";
import API from "../../utils/api";
import GetAQuoteButton from "../GetAQuoteButton";
import LoaderComp from "../LoaderComp";
import ProductCard from "../ProductCard";
import ProductCardDetailSkeleton from "../ProductCardDetailSkeleton";
import ProductCardSkeleton from "../ProductCardSkeleton";
import SliderComp from "../SliderComp";
import classes from "./ProductDetail.module.css";
import ProductImageZoom from "../ProductImageZoom";
import { PLACEHOLDER_IMAGE } from "../../utils/image";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function loadProduct() {
      setLoading(true);
      try {
        const res = await fetch(
          `${API.base}/getProduct.php?slug=${encodeURIComponent(slug)}`
        );
        const json = await res.json();
        if (mounted && res.ok && json.success) {
          setProduct(json.data);
          setSelectedImage(0);
          fetchRelatedProducts(json.data.category_slug, json.data.id);
        } else if (mounted) {
          setProduct(null);
          setRelatedProducts([]);
        }
      } catch (err) {
        if (mounted) {
          setProduct(null);
          setRelatedProducts([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }
    loadProduct();
    return () => {
      mounted = false;
    };
  }, [slug]);

  const fetchRelatedProducts = async (categorySlug, excludeId) => {
    if (!categorySlug) return setRelatedProducts([]);
    setRelatedLoading(true);
    try {
      const params = new URLSearchParams({
        action: "fetch",
        category_slug: categorySlug,
        limit: "12",
        status: "publish",
      });
      if (excludeId) params.append("exclude_id", excludeId);
      const res = await fetch(`${API.base}/products.php`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params,
      });
      const data = await res.json();
      const list = Array.isArray(data) ? data : data.data || [];
      setRelatedProducts(list);
    } catch (err) {
      setRelatedProducts([]);
    } finally {
      setRelatedLoading(false);
    }
  };

  // const getImageUrl = (img) => {
  //   if (!img) {
  //     const baseUrl = API.base.endsWith("/techmatter_api")
  //       ? API.base
  //       : `${API.base}/techmatter_api`;
  //     return `${baseUrl}/upload/placeholder-image.jpg`;
  //   }
  //   const baseUrl = API.base.endsWith("/techmatter_api")
  //     ? API.base
  //     : `${API.base}/techmatter_api`;
  //   return `${baseUrl}/upload/${img}`;
  // };

  // const getImageUrl = (img) => {
  //   const baseUrl = API.base.endsWith("/techmatter_api")
  //     ? API.base
  //     : `${API.base}/techmatter_api`;

  //   if (!img) {
  //     return `${baseUrl}/upload/placeholder-image.jpg`;
  //   }

  //   // Return actual image
  //   return `${baseUrl}/upload/${img}`;
  // };

  const getImageUrl = (img) => {
    const baseUrl = API.base.endsWith("/techmatter_api")
      ? API.base
      : `${API.base}/techmatter_api`;
    return img ? `${baseUrl}/upload/${img}` : PLACEHOLDER_IMAGE;
  };

  const galleryImages = useMemo(() => {
    if (!product) return [];
    let arr = [];
    if (product.productImage) arr.push(product.productImage);
    if (product.productGallery) {
      try {
        const gallery =
          typeof product.productGallery === "string"
            ? JSON.parse(product.productGallery)
            : product.productGallery;
        if (Array.isArray(gallery) && gallery.length > 0) {
          arr.push(...gallery);
        }
      } catch { }
    }
    return arr;
  }, [product]);

  const relatedSlides = useMemo(
    () =>
      relatedProducts.filter((p) => p.product_slug !== product?.product_slug),
    [relatedProducts, product]
  );


  if (loading)
    return (
      <div className={classes.centerWrap}>
        <LoaderComp />
      </div>
    );
  if (!product)
    return (
      <div className={classes.notFoundWrapper}>
        <div className={classes.notFoundCard}>
          <h2 className={classes.title}>Product Not Found</h2>
          <p className={classes.message}>
            The product you are looking for does not exist or has been removed.
          </p>
          <button className={classes.backButton} onClick={() => navigate(-1)}>
            ← Back to Shop
          </button>
        </div>
      </div>
    );

  const mainImage = galleryImages[selectedImage];
  const specs = [
    { label: "Manufacturer Part#", value: product.sku || "-" },
    { label: "Brand", value: product.brandName || "-" },
    { label: "Category", value: product.categoryName || "-" },
    {
      label: "Availability",
      value: product.availability || product.stock_status || "Contact",
    },
    { label: "Condition", value: product.condition || "New" },
  ];

  return (
    <div className={classes.productDetailWrapper}>
      {/* Helmet for SEO */}
      <Helmet>
        <title>{product.metaTitle || product.productName}</title>
        <meta
          name="description"
          content={
            product.metaDescription ||
            product.productShortDesc ||
            `Buy ${product.productName} online.`
          }
        />
        <link
          rel="canonical"
          href={`https://techmatter.co.uk/product/${product.product_slug}`}
        />
      </Helmet>
      {/* Breadcrumb */}
      <div className={classes.breadcrumbRow}>
        <div className={classes.breadcrumb}>
          <Link to="/">Home</Link> / <Link to="/shop">Shop</Link> /
          {product.category_slug && (
            <Link to={`/category/${product.category_slug}`}>
              {product.categoryName}
            </Link>
          )}{" "}
          /{" "}
          <span>
            {product.productName.length > 30
              ? product.productName.slice(0, 30) + "…"
              : product.productName}
            {/* {
              product?.product_slug.length > 70
                ? product?.product_slug.slice(0, 70) + "…"
                : product?.product_slug
            } */}
          </span>
        </div>

        <div className={classes.backRow}>
          <button className={classes.backButton} onClick={() => navigate(-1)}>
            ← Back to Shop
          </button>
        </div>
      </div>

      {loading ? (
        <div className={classes.skeletonWrapper}>
          <ProductCardDetailSkeleton />
        </div>
      ) : (
        <>
          {/* Two Columns */}
          <div className={classes.twoColumnRow}>
            {/* Left – Sticky Image Column */}
            <div className={classes.imageColumn}>
              <div className={classes.mainImageWrap}>
                {/* <img
                  src={getImageUrl(mainImage)}
                  alt={product.productName}
                  className={classes.mainImage}
                /> */}

                <ProductImageZoom
                  src={getImageUrl(mainImage)}
                  alt={product.productName}
                />
              </div>
              {galleryImages.length > 1 && (
                <div className={classes.thumbsRow}>
                  {galleryImages.map((img, idx) => (
                    <button
                      key={idx}
                      className={`${classes.thumbnail} ${idx === selectedImage ? classes.activeThumb : ""
                        }`}
                      onClick={() => setSelectedImage(idx)}
                    >
                      <img
                        src={getImageUrl(img)}
                        alt="thumbnail"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = PLACEHOLDER_IMAGE;
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right – Product Info */}
            <div className={classes.infoColumn}>
              <h1 className={classes.productName}>{product.productName}</h1>

              {product.productShortDesc && (
                <div className={classes.shortDesc}>
                  <p>{product.productShortDesc}</p>
                </div>
              )}

              {/* <div className={classes.priceWrap}>
                {product.salePrice && parseFloat(product.salePrice) > 0 ? (
                  <>
                    <div className={classes.regularPrice}>
                      £{Number(product.regularPrice).toFixed(2)}
                    </div>
                    <div className={classes.salePrice}>
                      £{Number(product.salePrice).toFixed(2)}
                    </div>
                  </>
                ) : (
                  <div className={classes.currentPrice}>
                    £{Number(product.regularPrice || 0).toFixed(2)}
                  </div>
                )}
              </div> */}

              {/* Specs Table */}
              <div className={classes.specsTable}>
                {specs.map((s, idx) => (
                  <div key={idx} className={classes.specRow}>
                    <div className={classes.specLabel}>{s.label}</div>
                    <div className={classes.specValue}>{s.value}</div>
                  </div>
                ))}
              </div>

              <p className={classes.extraText}>
                Looking for a large quantity of <span>{product.sku}</span>? Call
                us at <Link to="tel:++442031293546">+44 20 3129 3546</Link> or
                Request a Quote below and one of our sales representative will
                get in touch with you very soon.
              </p>

              <div className={classes.actionsRow}>
                <GetAQuoteButton sku={product.sku} text={"Request a Quote"} />
              </div>
            </div>

            {/* Benefits Section */}
            <div className={classes.infoColumn}>
              {/* <h1 className={classes.shippingName}>Fast Shipping</h1> */}
              <div className={classes.benefitsBottom}>
                <div className={classes.benefitCard}>
                  <div className={classes.benefitIcon}>
                    <img
                      src="../img/sidebar-icon/free-delivery.svg"
                      alt="free-delivery"
                    />
                  </div>

                  <div className={classes.benefitText}>
                    <h4>Free Delivery</h4>
                    <p>From all orders over $10</p>
                  </div>
                </div>

                <div className={classes.benefitCard}>
                  <div className={classes.benefitIcon}>
                    <img src="../img/sidebar-icon/support.svg" alt="support" />
                  </div>

                  <div className={classes.benefitText}>
                    <h4>Support 24/7</h4>
                    <p>Shop with an expert</p>
                  </div>
                </div>

                <div className={classes.benefitCard}>
                  <div className={classes.benefitIcon}>
                    <img
                      src="../img/sidebar-icon/return-refund.svg"
                      alt="return-refund"
                    />
                  </div>

                  <div className={classes.benefitText}>
                    <h4>Return & Refund</h4>
                    <p>Free return over $200</p>
                  </div>
                </div>

                <div className={classes.benefitCard}>
                  <div className={classes.benefitIcon}>
                    <img
                      src="../img/sidebar-icon/secure-payment.svg"
                      alt="secure-payment"
                    />
                  </div>

                  <div className={classes.benefitText}>
                    <h4>Secure payment</h4>
                    <p>100% Protected</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className={classes.tabsRow}>
            <div className={classes.tabsHeader}>
              <button className={`${classes.tabBtn} ${classes.activeTab}`}>
                Description
              </button>
            </div>
            <div className={classes.tabContent}>
              {product.productDesc ? (
                <div
                  className={classes.descriptionHtml}
                  dangerouslySetInnerHTML={{ __html: product.productDesc }}
                />
              ) : (
                <p>No description available.</p>
              )}
            </div>
          </div>
        </>
      )}

      {/* Related Products */}
      {relatedSlides.length > 0 && (
        <section className={classes.relatedSection}>
          <h2>Related Products</h2>
          {relatedLoading ? (
            <div className={classes.skeletonWrapper}>
              <ProductCardSkeleton count={4} />
            </div>
          ) : (
            <SliderComp slidesToShow={4} arrows dots={false} gap={20}>
              {relatedSlides.map((related) => (
                <div key={related.id} className={classes.relatedCardWrapper}>
                  <ProductCard
                    product={{
                      ...related,
                      productImage: getImageUrl(related.productImage),
                    }}
                  />
                </div>
              ))}
            </SliderComp>
          )}
        </section>
      )}
    </div>
  );
}
